"use client";

import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Phone } from "lucide-react";
import { useEffect, useRef, useState } from "react";
export default function OurStory() {
  const {
    ref: textRef,
    isVisible: textVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.3
  });
  const imageRef = useRef<HTMLDivElement>(null);
  const [parallaxOffset, setParallaxOffset] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      if (!imageRef.current) return;
      const rect = imageRef.current.getBoundingClientRect();
      const scrollProgress = (window.innerHeight - rect.top) / (window.innerHeight + rect.height);
      setParallaxOffset(scrollProgress * 50);
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <section id="our-story" className="bg-background py-16 lg:py-24 overflow-hidden">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {}
          <div ref={imageRef} className="relative h-[400px] lg:h-[500px] rounded-[var(--radius-lg)] overflow-hidden shadow-medium">
            <div className="absolute inset-0 bg-cover bg-center transition-transform duration-75 ease-linear" style={{
            backgroundImage: "url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1541246/img/therapy_session_compassionate_moment.png)",
            transform: `translateY(${parallaxOffset}px)`
          }} />
          </div>

          {}
          <div ref={textRef} className="space-y-6">
            <div className={`transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${textVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-[10px]"}`}>
              <h2 className="text-primary mb-4">Our Story</h2>
              <h3 className="text-foreground mb-6">
                Transforming Lives with Compassion and Evidence-Based Care
              </h3>
            </div>

            <div className={`space-y-4 transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] delay-100 ${textVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-[10px]"}`} style={{
            transitionDelay: textVisible ? "100ms" : "0ms"
          }}>
              <p className="text-foreground text-base lg:text-lg leading-relaxed">
                At ABA With Passion Inc, we believe every child deserves the opportunity to reach their full potential. Founded in the heart of Miami, our practice is built on a foundation of compassion, cultural sensitivity, and evidence-based Applied Behavior Analysis.
              </p>
              <p className="text-foreground text-base lg:text-lg leading-relaxed">
                Our bilingual team (English/Spanish) brings over a decade of experience in supporting children and adolescents with autism and developmental needs. We work hand-in-hand with families, providing in-home services, school-based support, and comprehensive parent training to create lasting, meaningful change.
              </p>
            </div>

            <div className={`flex flex-col sm:flex-row gap-4 pt-4 transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] delay-200 ${textVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-[10px]"}`} style={{
            transitionDelay: textVisible ? "200ms" : "0ms"
          }}>
              <Link to="/GetStartedPage">
                <Button size="lg" className="w-full sm:w-auto shadow-soft hover:shadow-medium hover:scale-[1.02] active:scale-[0.98] transition-all duration-[var(--duration-fast)]">
                  Request Services
                </Button>
              </Link>
              <Link to="tel:+13051234567">
                <Button variant="outline" size="lg" className="w-full sm:w-auto shadow-soft hover:shadow-medium hover:scale-[1.02] active:scale-[0.98] transition-all duration-[var(--duration-fast)]">
                  <Phone className="size-5" />
                  Call Us
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>;
}